import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Clock } from '../components/Clock';
import { ComicBoard, comicSrc } from '../components/ComicBoard';
import { JokerBanner } from '../components/JokerBanner';
import { StoryText } from '../components/StoryText';
import { parseConfigText, serializeConfigText } from '../../shared/configText';
import { BEAM_FONTS, MAX_FONT_SCALE, MIN_FONT_SCALE, beamFont } from '../../shared/fonts';
import { formatClock, formatDuration } from '../../shared/format';
import {
  COMIC_PANELS,
  countWords,
  type GameEvent,
  type JokerTarget,
  type PublicState,
  type RoundKind,
} from '../../shared/types';
import type { ClientMessage, ModAction } from '../../shared/protocol';
import { useFollowScroll } from '../lib/followScroll';
import { remainingNow, useSession, useTicker } from '../lib/socket';
import { useGameSounds } from '../lib/useGameSounds';
import { moderatorLink, storedModeratorToken } from '../lib/router';

/**
 * Moderator / host view (R-2): configure, start, police and end the game,
 * run the joker deck, run the Bildaufgabe warm-up, and export each finished
 * round. Designed to run on the device that also drives the projection
 * (decision D5) — the button in the header opens the projection as a second
 * window for the beamer.
 *
 * The moderation is not tied to that one device (2026-09-08): the header
 * offers a shareable link that carries the moderation token, so a second
 * moderator backstage can correct clocks or pull PDFs while the host is on
 * stage. Every moderation device sees the same authoritative state.
 */
export function ModeratorView({ pin }: { pin: string }) {
  const token = storedModeratorToken(pin);
  const session = useSession(pin, 'moderator', { token });
  const now = useTicker(200);
  const sound = useGameSounds(session.state, 'moderator');

  if (!token) {
    return (
      <div className="fullmsg">
        <h1>Moderations-Zugang fehlt</h1>
        <p>
          Diese Ansicht gehört zu dem Gerät, das die Session erstellt hat. Neue Session auf der Startseite anlegen —
          oder den Moderations-Link von einem Gerät öffnen, das schon in der Moderation ist.
        </p>
        <a className="btn btn--ghost" href="#/">
          Zur Startseite
        </a>
      </div>
    );
  }
  if (session.fatalError) {
    return (
      <div className="fullmsg">
        <h1>Kein Zugriff</h1>
        <p>{session.fatalError}</p>
        <a className="btn btn--ghost" href="#/">
          Zur Startseite
        </a>
      </div>
    );
  }
  if (!session.state) {
    return (
      <div className="fullmsg">
        <h1>Verbinden…</h1>
        <p>Session {pin}</p>
      </div>
    );
  }

  const state = session.state;
  const round = state.round;
  const mod = (action: ModAction) => session.send({ type: 'mod', action });
  // Bildrunden count towards the evening's numbering (2026-09-09).
  const roundLabel =
    round.kind === 'comic' ? `Bildrunde ${round.number}` : `Runde ${round.number} / ${state.plannedTotal}`;

  return (
    <div className="mod">
      <header className="mod__head">
        <div className="mod__brand">
          Schreibarena
          <span className="mod__role">Moderation</span>
        </div>
        <div className="mod__session">
          <span className="mod__pin" data-testid="session-pin">
            PIN {pin}
          </span>
          <span className={`badge ${session.status === 'ready' ? 'badge--ok' : 'badge--warn'}`}>
            {session.status === 'ready' ? 'verbunden' : 'Verbindung…'}
          </span>
          <span className="badge" data-testid="round-badge">
            {roundLabel}
          </span>
          <button
            className="btn btn--small"
            onClick={() => sound.setEnabled(!sound.enabled)}
            title="Tonwiedergabe auf diesem Gerät"
            data-testid="sound-toggle"
          >
            {sound.enabled ? '🔊' : '🔇'}
          </button>
          <ShareLinkButton pin={pin} token={token} />
          <button
            className="btn btn--small"
            onClick={() => window.open(`#/beam/${pin}`, 'schreibarena-beam', 'noopener')}
            data-testid="open-projection"
          >
            Projektion öffnen ↗
          </button>
        </div>
      </header>

      {round.phase === 'idle' && <SetupPanel pin={pin} token={token} state={state} mod={mod} />}
      {round.phase === 'intro' && <IntroPanel pin={pin} state={state} mod={mod} />}
      {(round.phase === 'running' || round.phase === 'paused') && (
        <LivePanel
          pin={pin}
          state={state}
          mod={mod}
          send={session.send}
          now={now}
          clockOffset={session.clockOffset}
          stateReceivedAt={session.stateReceivedAt}
        />
      )}
      {round.phase === 'ended' && <EndPanel pin={pin} token={token} state={state} mod={mod} />}

      {session.denied && (
        <div className="toast" role="status" data-testid="denied-toast">
          {session.denied}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------- shared moderation --- */

/**
 * Copyable moderation link (2026-09-08). A second moderator opens it on a
 * phone or laptop and lands in this same view with the same rights.
 */
function ShareLinkButton({ pin, token }: { pin: string; token: string }) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const url = moderatorLink(pin, token);

  useEffect(() => {
    if (!showQr) return;
    QRCode.toDataURL(url, { margin: 1, width: 240, color: { dark: '#111111', light: '#f4f2ec' } })
      .then(setQr)
      .catch(() => setQr(null));
  }, [showQr, url]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Moderations-Link kopieren:', url);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <span className="sharelink">
      <button className="btn btn--small" onClick={copy} data-testid="copy-mod-link">
        {copied ? '✓ Link kopiert' : '⧉ Moderations-Link'}
      </button>
      <button
        className="btn btn--small btn--ghost"
        onClick={() => setShowQr((v) => !v)}
        aria-label="Moderations-Link als QR-Code"
        data-testid="mod-link-qr"
      >
        QR
      </button>
      {showQr && (
        <div className="sharelink__pop">
          <p>Zweites Moderations-Gerät — dieser Link öffnet die Moderation mit allen Rechten.</p>
          {qr && <img className="qr" src={qr} alt="QR-Code für den Moderations-Link" />}
          <code className="sharelink__url">{url}</code>
        </div>
      )}
    </span>
  );
}

/* ------------------------------------------------------------- Setup ---- */

function SetupPanel({
  pin,
  token,
  state,
  mod,
}: {
  pin: string;
  token: string;
  state: PublicState;
  mod: (a: ModAction) => void;
}) {
  const round = state.round;
  const [firstAuthor, setFirstAuthor] = useState(0);
  const [prompt, setPrompt] = useState(round.prompt);
  const seatsFull = state.authors.length >= state.maxAuthors;
  const joinUrl = `${location.origin}/#/write/${pin}`;
  const [qr, setQr] = useState<string | null>(null);
  const comicChosen = round.kind !== 'comic' || round.comicId !== null;

  useEffect(() => setPrompt(round.prompt), [round.prompt, round.kind]);

  useEffect(() => {
    QRCode.toDataURL(joinUrl, { margin: 1, width: 220, color: { dark: '#111111', light: '#f4f2ec' } })
      .then(setQr)
      .catch(() => setQr(null));
  }, [joinUrl]);

  return (
    <div className="mod__grid">
      <section className="card">
        <span className="card__no">01</span>
        <h2>Beitritt</h2>
        <p>
          Autor:innen-Geräte öffnen <strong>{joinUrl.replace(/^https?:\/\//, '')}</strong>
        </p>
        {qr && <img className="qr" src={qr} alt={`QR-Code für ${joinUrl}`} />}
        <ul className="seats" data-testid="seat-list">
          {Array.from({ length: state.maxAuthors }, (_, i) => {
            const seat = state.authors[i];
            return (
              <li key={i} className={`seat ${seat ? 'seat--taken' : ''}`} style={seat ? { borderColor: seat.color } : {}}>
                <span className="seat__label">Autor:in {i + 1}</span>
                <span className="seat__name">{seat ? seat.name : 'wartet auf Beitritt…'}</span>
                {seat && <span className={`badge ${seat.connected ? 'badge--ok' : 'badge--warn'}`}>{seat.connected ? 'online' : 'offline'}</span>}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="card">
        <span className="card__no">02</span>
        <h2>Format dieser Runde</h2>
        <div className="radio-row radio-row--big">
          {(['comic', 'story'] as RoundKind[]).map((k) => (
            <label key={k} className={`radio ${round.kind === k ? 'radio--on' : ''}`}>
              <input
                type="radio"
                name="roundkind"
                checked={round.kind === k}
                onChange={() => mod({ op: 'set_round_kind', kind: k })}
              />
              {k === 'comic' ? 'Bildaufgabe (Warm-up)' : 'Schreibrunde'}
            </label>
          ))}
        </div>
        <p className="hint">
          {round.kind === 'comic'
            ? 'Warm-up: die beiden Autor:innen beschriften abwechselnd die sechs Panels eines Comics — Panel 1 links oben, dann rechts oben, und so weiter. Beliebig viele Bildrunden vor den Schreibrunden.'
            : 'Die klassische Schreibrunde: eine Geschichte, eine Schachuhr, kein Löschen.'}
        </p>
        <label className="field">
          <span>Schreibimpuls dieser Runde</span>
          <input
            className="input"
            value={prompt}
            maxLength={200}
            placeholder={
              round.kind === 'comic'
                ? 'z. B. „Beschriftet die sechs Panels dieses Comics“'
                : 'z. B. „Der letzte Zug fährt um Mitternacht“'
            }
            onChange={(e) => setPrompt(e.target.value)}
            onBlur={() => mod({ op: 'set_prompt', prompt })}
            data-testid="prompt-input"
          />
        </label>
      </section>

      {round.kind === 'comic' && <ComicLibrary pin={pin} token={token} state={state} mod={mod} />}

      <section className="card">
        <span className="card__no">03</span>
        <h2>Spielregeln</h2>
        <ConfigEditor key={JSON.stringify(state.config)} state={state} mod={mod} />
      </section>

      <DisplayPanel state={state} mod={mod} />

      <section className="card">
        <span className="card__no">04</span>
        <h2>Jokerdeck</h2>
        <DeckEditor state={state} mod={mod} playable={false} target="global" />
      </section>

      <section className="card">
        <span className="card__no">05</span>
        <h2>Konfig speichern / laden</h2>
        <ConfigTextPanel state={state} mod={mod} />
      </section>

      <section className="card card--start">
        <span className="card__no">06</span>
        <h2>Start</h2>
        <div className="field">
          <span>Wer beginnt?</span>
          <div className="radio-row">
            {Array.from({ length: state.maxAuthors }, (_, i) => (
              <label key={i} className={`radio ${firstAuthor === i ? 'radio--on' : ''}`}>
                <input type="radio" name="first" checked={firstAuthor === i} onChange={() => setFirstAuthor(i)} />
                {state.authors[i]?.name ?? `Autor:in ${i + 1}`}
              </label>
            ))}
          </div>
        </div>
        <button
          className="btn btn--primary btn--big"
          disabled={!seatsFull || !comicChosen}
          onClick={() => mod({ op: 'show_intro', firstAuthor })}
          data-testid="show-intro"
        >
          {!seatsFull
            ? 'Warten auf beide Autor:innen…'
            : !comicChosen
              ? 'Erst einen Comic auswählen…'
              : 'Aufgabe zeigen →'}
        </button>
        <p className="hint">
          Zeigt die Aufgabe auf Projektion und Autor:innen-Geräten. Die Uhren laufen erst, wenn du danach „Jetzt
          starten“ drückst.
        </p>
      </section>
    </div>
  );
}

/* ------------------------------------------------------- comic library -- */

/**
 * The session's comic shelf (2026-09-08): upload as many JPEGs as the
 * evening needs, each with a title, and pick any of them for any Bildrunde
 * in any order. They live as long as the session does.
 *
 * The browser measures the file before it goes up and proposes the source
 * grid from the aspect ratio (2×3 portrait / 3×2 landscape); the moderator
 * can flip that if a strip is laid out the other way. Whatever the source,
 * the views re-tile it into the canonical 2×3.
 */
function ComicLibrary({
  pin,
  token,
  state,
  mod,
}: {
  pin: string;
  token: string;
  state: PublicState;
  mod: (a: ModAction) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const selected = state.comics.find((c) => c.id === state.round.comicId);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const size = await measure(file);
        // Landscape sheets are 3 across and 2 down; portrait ones 2 and 3.
        const cols = size.width / size.height > 1.15 ? 3 : 2;
        const rows = COMIC_PANELS / cols;
        const title = file.name.replace(/\.[a-z0-9]+$/i, '').slice(0, 80);
        const url =
          `/api/sessions/${pin}/comics?token=${encodeURIComponent(token)}` +
          `&title=${encodeURIComponent(title)}&cols=${cols}&rows=${rows}` +
          `&width=${size.width}&height=${size.height}`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `Upload fehlgeschlagen (${res.status}).`);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload fehlgeschlagen.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Comic aus der Session entfernen?')) return;
    const res = await fetch(`/api/sessions/${pin}/comics/${encodeURIComponent(id)}?token=${encodeURIComponent(token)}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setError(body.error ?? 'Löschen fehlgeschlagen.');
    }
  };

  return (
    <section className="card card--wide">
      <span className="card__no">Bild</span>
      <h2>Comic-Bibliothek</h2>
      <div className="btn-row">
        <input
          ref={fileRef}
          className="filein"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={(e) => void upload(e.target.files)}
          aria-label="Comics hochladen"
          data-testid="comic-upload"
        />
        {busy && <span className="badge badge--warn">lädt hoch…</span>}
      </div>
      {error && <p className="form-error">{error}</p>}
      {state.comics.length === 0 ? (
        <p className="hint">Noch keine Comics. JPEGs hochladen — pro Blatt sechs Panels, quer (3×2) oder hoch (2×3).</p>
      ) : (
        <ul className="comiclist" data-testid="comic-list">
          {state.comics.map((c) => (
            <li key={c.id} className={`comiclist__item ${c.id === state.round.comicId ? 'comiclist__item--on' : ''}`}>
              <button
                className="comiclist__pick"
                onClick={() => mod({ op: 'set_round_comic', comicId: c.id })}
                data-testid={`pick-comic-${c.id}`}
              >
                <img className="comiclist__thumb" src={comicSrc(pin, c.id)} alt="" />
                <span className="comiclist__title">{c.title}</span>
              </button>
              <span className="comiclist__grid">
                {c.cols}×{c.rows}
              </span>
              <button
                className="btn btn--small btn--ghost"
                title="Raster drehen (2×3 ↔ 3×2)"
                onClick={() => mod({ op: 'set_comic_grid', id: c.id, cols: c.rows, rows: c.cols })}
                data-testid={`flip-comic-${c.id}`}
              >
                ⟲
              </button>
              <button
                className="btn btn--small btn--ghost"
                title="Titel ändern"
                onClick={() => {
                  const title = window.prompt('Titel des Comics', c.title);
                  if (title !== null && title.trim()) mod({ op: 'rename_comic', id: c.id, title });
                }}
              >
                ✎
              </button>
              <button className="btn btn--small btn--ghost" title="Entfernen" onClick={() => void remove(c.id)}>
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      {selected && (
        <div className="comicpreview">
          <span className="card__no">Vorschau — {selected.title}</span>
          <ComicBoard
            comic={selected}
            src={comicSrc(pin, selected.id)}
            round={state.round}
            authors={state.authors}
            captions={false}
            className="comicboard--mini"
          />
        </div>
      )}
    </section>
  );
}

function measure(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name} ist kein lesbares Bild.`));
    };
    img.src = url;
  });
}

/* ------------------------------------------------------------- Intro ---- */

/**
 * The task slide (2026-09-08). Audience and authors read the assignment —
 * the Schreibimpuls, or the comic — while nothing is running. The moderator
 * starts the clocks when the room has understood it.
 */
function IntroPanel({ pin, state, mod }: { pin: string; state: PublicState; mod: (a: ModAction) => void }) {
  const round = state.round;
  const comic = round.comicId === null ? undefined : state.comics.find((c) => c.id === round.comicId);
  const first = state.authors[round.activeAuthor];

  return (
    <div className="mod__grid">
      <section className="card card--wide card--start">
        <span className="card__no">Intro</span>
        <h2>{round.kind === 'comic' ? `Bildrunde ${round.number}` : `Runde ${round.number}`} — Aufgabe steht</h2>
        <p className="introprompt" data-testid="intro-prompt">
          {round.prompt || '(kein Schreibimpuls gesetzt)'}
        </p>
        <p className="hint">
          Projektion und Autor:innen-Geräte zeigen die Aufgabe. <strong>{first?.name ?? 'Autor:in 1'}</strong> beginnt.
          Die Uhren laufen erst mit dem Start.
        </p>
        <div className="field">
          <span>Wer beginnt?</span>
          <div className="radio-row">
            {Array.from({ length: state.maxAuthors }, (_, i) => (
              <label key={i} className={`radio ${round.activeAuthor === i ? 'radio--on' : ''}`}>
                <input
                  type="radio"
                  name="intro-first"
                  checked={round.activeAuthor === i}
                  onChange={() => mod({ op: 'show_intro', firstAuthor: i })}
                />
                {state.authors[i]?.name ?? `Autor:in ${i + 1}`}
              </label>
            ))}
          </div>
        </div>
        <div className="btn-row">
          <button className="btn btn--primary btn--big" onClick={() => mod({ op: 'start_round' })} data-testid="start-round">
            ▶ Jetzt starten — Uhren laufen
          </button>
          <button className="btn" onClick={() => mod({ op: 'back_to_setup' })} data-testid="back-to-setup">
            ← Zurück zur Vorbereitung
          </button>
        </div>
      </section>

      {round.kind === 'comic' && comic && (
        <section className="card card--wide">
          <span className="card__no">Comic</span>
          <h2>{comic.title}</h2>
          <ComicBoard
            comic={comic}
            src={comicSrc(pin, comic.id)}
            round={round}
            authors={state.authors}
            captions={false}
            className="comicboard--mini"
          />
        </section>
      )}

      <DisplayPanel state={state} mod={mod} />
    </div>
  );
}

function ConfigEditor({ state, mod }: { state: PublicState; mod: (a: ModAction) => void }) {
  const c = state.config;
  const [startMin, setStartMin] = useState(String(c.startMs / 60000));
  const [penaltyS, setPenaltyS] = useState(String(c.penaltyStepMs / 1000));
  const [jokerS, setJokerS] = useState(String(c.jokerDefaultMs / 1000));
  const [rounds, setRounds] = useState(String(c.roundsPlanned));

  const commit = () => {
    mod({
      op: 'set_config',
      config: {
        startMs: Math.round(Number(startMin.replace(',', '.')) * 60000) || c.startMs,
        penaltyStepMs: Math.round(Number(penaltyS) * 1000) || c.penaltyStepMs,
        jokerDefaultMs: Math.round(Number(jokerS) * 1000) || c.jokerDefaultMs,
        roundsPlanned: Number(rounds) || c.roundsPlanned,
      },
    });
  };

  return (
    <div className="config" onBlur={commit}>
      <label className="field field--inline">
        <span>Zeit pro Autor:in (Minuten)</span>
        <input className="input input--num" inputMode="decimal" value={startMin} onChange={(e) => setStartMin(e.target.value)} data-testid="config-start-min" />
      </label>
      <label className="field field--inline">
        <span>Strafschritt (Sekunden)</span>
        <input className="input input--num" inputMode="numeric" value={penaltyS} onChange={(e) => setPenaltyS(e.target.value)} />
      </label>
      <label className="field field--inline">
        <span>Jokerdauer Standard (Sekunden)</span>
        <input className="input input--num" inputMode="numeric" value={jokerS} onChange={(e) => setJokerS(e.target.value)} />
      </label>
      <label className="field field--inline">
        <span>Geplante Schreibrunden</span>
        <input className="input input--num" inputMode="numeric" value={rounds} onChange={(e) => setRounds(e.target.value)} />
      </label>
    </div>
  );
}

/**
 * Export/import the game rules + joker deck as human-readable text
 * (2026-07-18 requirement): configs can be saved, archived and reused
 * between evenings. Format documented in src/shared/configText.ts.
 */
function ConfigTextPanel({ state, mod }: { state: PublicState; mod: (a: ModAction) => void }) {
  const [text, setText] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const doExport = () => {
    setText(serializeConfigText(state.config, state.jokerDeck, new Date().toISOString()));
    setMsg('Aktuelle Konfiguration exportiert — Text kopieren oder als Datei sichern.');
  };

  const doDownload = () => {
    const content = text || serializeConfigText(state.config, state.jokerDeck, new Date().toISOString());
    const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `schreibarena-konfig-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = () => {
    const parsed = parseConfigText(text);
    if (!parsed.ok) {
      setMsg(parsed.errors.join('\n'));
      return;
    }
    if (Object.keys(parsed.value.config).length > 0) {
      mod({ op: 'set_config', config: parsed.value.config });
    }
    if (parsed.value.deck.length > 0) {
      mod({ op: 'set_deck', deck: parsed.value.deck });
    }
    setMsg(`Übernommen: ${Object.keys(parsed.value.config).length} Regeln, ${parsed.value.deck.length} Joker.`);
  };

  return (
    <div className="configtext">
      <textarea
        className="configtext__area"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={'Hierhin exportieren — oder eine gespeicherte Konfiguration einfügen und übernehmen.'}
        spellCheck={false}
        aria-label="Konfigurationstext"
        data-testid="configtext-area"
      />
      <div className="btn-row">
        <button className="btn btn--small" onClick={doExport} data-testid="configtext-export">
          ⬆ In Feld exportieren
        </button>
        <button className="btn btn--small" onClick={doDownload} data-testid="configtext-download">
          ⬇ Als Datei
        </button>
        <button className="btn btn--small btn--primary" onClick={doImport} disabled={!text.trim()} data-testid="configtext-import">
          Übernehmen
        </button>
      </div>
      {msg && (
        <p className="configtext__msg" data-testid="configtext-msg">
          {msg}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- Live ---- */

function LivePanel(props: {
  pin: string;
  state: PublicState;
  mod: (a: ModAction) => void;
  send: (m: ClientMessage) => void;
  now: number;
  clockOffset: number;
  stateReceivedAt: number;
}) {
  const { pin, state, mod, now, clockOffset, stateReceivedAt } = props;
  const round = state.round;
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState<JokerTarget>('global');
  const paused = round.phase === 'paused';
  const elapsed = round.startedAt === null ? 0 : Math.max(0, now + clockOffset - round.startedAt);
  const comic = round.comicId === null ? undefined : state.comics.find((c) => c.id === round.comicId);

  return (
    <div className="mod__live">
      <section className="mod__clocks">
        {Array.from({ length: state.maxAuthors }, (_, i) => (
          <Clock
            key={i}
            seat={state.authors[i]}
            seatIndex={i}
            round={round}
            remainingMs={remainingNow(state, stateReceivedAt, now, i)}
            size="M"
          />
        ))}
        <div className="mod__gamestats">
          <div>
            <strong data-testid="stat-elapsed">{formatClock(elapsed)}</strong> gespielt
          </div>
          {round.kind === 'comic' ? (
            <div>
              Panel <strong data-testid="stat-panel">{Math.min(round.panelStarts.length, COMIC_PANELS)}</strong> von{' '}
              {COMIC_PANELS}
            </div>
          ) : (
            <div>
              <strong>{round.text.length}</strong> Zeichen · <strong>{countWords(round.text)}</strong> Wörter
            </div>
          )}
          <div>
            <strong>{round.keystrokeCount}</strong> Anschläge geloggt
          </div>
        </div>
      </section>

      <section className="mod__controls">
        <button className={`btn ${paused ? 'btn--primary' : ''}`} onClick={() => mod({ op: paused ? 'resume' : 'pause' })} data-testid="pause-resume">
          {paused ? '▶ Weiter' : '⏸ Pause'}
        </button>
        <button className="btn" disabled={!round.undoAvailable} onClick={() => mod({ op: 'undo_pass' })} data-testid="undo-pass">
          ↩ Zugwechsel zurücknehmen
        </button>
        <button
          className="btn btn--danger"
          onClick={() => {
            if (window.confirm('Runde wirklich sofort beenden?')) mod({ op: 'force_end' });
          }}
          data-testid="force-end"
        >
          ■ Runde beenden
        </button>
      </section>

      <div className="mod__grid mod__grid--live">
        <section className="card">
          <span className="card__no">Regeln</span>
          <h2>Strafen & Fertig</h2>
          <label className="field">
            <span>Grund (fürs Protokoll)</span>
            <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Adjektiv trotz Joker" data-testid="penalty-reason" />
          </label>
          {Array.from({ length: state.maxAuthors }, (_, i) => {
            const seat = state.authors[i];
            const confirmed = round.confirmedDone[i] === true;
            return (
              <div className="authorops" key={i} style={{ borderColor: seat?.color ?? '#111' }}>
                <span className="authorops__name">{seat?.name ?? `Autor:in ${i + 1}`}</span>
                <button
                  className="btn btn--small btn--danger"
                  onClick={() => {
                    mod({ op: 'penalty', author: i, reason: reason || undefined });
                    setReason('');
                  }}
                  data-testid={`penalty-${i}`}
                >
                  −{Math.round(state.config.penaltyStepMs / 1000)} s Strafe
                </button>
                <button className="btn btn--small" onClick={() => mod({ op: 'adjust_clock', author: i, deltaMs: state.config.penaltyStepMs })} data-testid={`credit-${i}`}>
                  +{Math.round(state.config.penaltyStepMs / 1000)} s
                </button>
                <button className={`btn btn--small ${confirmed ? 'btn--confirmed' : ''}`} onClick={() => mod({ op: 'confirm_for', author: i, value: !confirmed })} data-testid={`confirm-for-${i}`}>
                  {confirmed ? 'Fertig ✓' : 'Fertig f. sie/ihn'}
                </button>
              </div>
            );
          })}
        </section>

        <section className="card">
          <span className="card__no">Joker</span>
          <h2>Jokerdeck</h2>
          <div className="field">
            <span>Ziel</span>
            <div className="radio-row">
              <label className={`radio ${target === 'global' ? 'radio--on' : ''}`}>
                <input type="radio" checked={target === 'global'} onChange={() => setTarget('global')} />
                Beide
              </label>
              {Array.from({ length: state.maxAuthors }, (_, i) => (
                <label key={i} className={`radio ${target === i ? 'radio--on' : ''}`}>
                  <input type="radio" checked={target === i} onChange={() => setTarget(i)} />
                  {state.authors[i]?.name ?? `Autor:in ${i + 1}`}
                </label>
              ))}
            </div>
          </div>
          <DeckEditor state={state} mod={mod} playable target={target} />
          <JokerBanner
            round={round}
            authors={state.authors}
            now={now}
            clockOffset={clockOffset}
            onEnd={(instanceId) => mod({ op: 'end_joker', instanceId })}
          />
        </section>

        <DisplayPanel state={state} mod={mod} />

        <section className="card card--wide">
          <span className="card__no">Live</span>
          <h2>{round.kind === 'comic' ? 'Bildunterschriften & Protokoll' : 'Text & Protokoll'}</h2>
          {round.prompt && <div className="prompt">Impuls: {round.prompt}</div>}
          {round.kind === 'comic' && comic ? (
            <ComicBoard
              comic={comic}
              src={comicSrc(pin, comic.id)}
              round={round}
              authors={state.authors}
              showCaret={round.phase === 'running'}
              className="comicboard--mini"
            />
          ) : (
            <PreviewFollow textLen={round.text.length} fontStack={beamFont(state.display.fontId).stack}>
              <StoryText round={round} authors={state.authors} showCaret />
            </PreviewFollow>
          )}
          <EventLog events={round.recentEvents} state={state} />
        </section>
      </div>
    </div>
  );
}

/**
 * Live typography (2026-08-09, extended 2026-09-08): switch the story font
 * (20 curated faces, each button rendered in its own face) and scale its
 * size. Available in every phase — before the round to set it up, and at any
 * moment during play — and the change is pushed to the projection *and* the
 * author devices with the next state broadcast.
 */
function DisplayPanel({ state, mod }: { state: PublicState; mod: (a: ModAction) => void }) {
  const d = state.display;
  const pct = Math.round(d.fontScale * 100);
  const setScale = (scale: number) =>
    mod({ op: 'set_display', display: { fontScale: Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, scale)) } });

  return (
    <section className="card">
      <span className="card__no">Schrift</span>
      <h2>Schrift — live auf allen Geräten</h2>
      <p className="hint">
        Wirkt sofort auf Projektion und Autor:innen-Geräte — vor der Runde und mitten im Spiel.
      </p>
      <div className="field">
        <span>Schriftart</span>
        <div className="fontgrid" data-testid="font-grid">
          {BEAM_FONTS.map((f) => (
            <button
              key={f.id}
              className={`fontbtn ${d.fontId === f.id ? 'fontbtn--on' : ''}`}
              style={{ fontFamily: f.stack }}
              title={f.hint}
              onClick={() => mod({ op: 'set_display', display: { fontId: f.id } })}
              data-testid={`font-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span>
          Schriftgröße — <strong data-testid="font-scale-value">{pct} %</strong>
        </span>
        <div className="sizerow">
          <button className="btn btn--small" onClick={() => setScale(d.fontScale - 0.1)} aria-label="Schrift kleiner" data-testid="font-smaller">
            A−
          </button>
          <input
            className="sizerange"
            type="range"
            min={MIN_FONT_SCALE * 100}
            max={MAX_FONT_SCALE * 100}
            step={5}
            value={pct}
            onChange={(e) => setScale(Number(e.target.value) / 100)}
            aria-label="Schriftgröße"
            data-testid="font-scale"
          />
          <button className="btn btn--small" onClick={() => setScale(d.fontScale + 0.1)} aria-label="Schrift größer" data-testid="font-larger">
            A+
          </button>
          <button className="btn btn--small btn--ghost" onClick={() => setScale(1)} data-testid="font-scale-reset">
            100 %
          </button>
        </div>
      </div>
    </section>
  );
}

/** Small scrolling text preview that follows the newest line. */
function PreviewFollow({ textLen, fontStack, children }: { textLen: number; fontStack?: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useFollowScroll(ref, textLen);
  return (
    <div ref={ref} className="story-preview-box" style={fontStack ? ({ '--story-font': fontStack } as React.CSSProperties) : undefined}>
      {children}
    </div>
  );
}

/* --------------------------------------------------------------- End ---- */

function EndPanel({ pin, token, state, mod }: { pin: string; token: string; state: PublicState; mod: (a: ModAction) => void }) {
  const round = state.round;
  const zipUrl = (n: number) => `/api/sessions/${pin}/rounds/${n}/export.zip?token=${encodeURIComponent(token)}`;
  const pdfUrl = (n: number) => `/api/sessions/${pin}/rounds/${n}/export.pdf?token=${encodeURIComponent(token)}`;
  const morePlanned = round.kind === 'story' && round.number < state.plannedTotal;
  const comic = round.comicId === null ? undefined : state.comics.find((c) => c.id === round.comicId);
  const warmup = round.kind === 'comic';

  return (
    <div className="mod__grid">
      <section className="card card--wide">
        <span className="card__no">Ende</span>
        <h2>
          {warmup ? `Bildrunde ${round.number}` : `Runde ${round.number}`} beendet —{' '}
          {round.endReason === 'time'
            ? 'Zeit abgelaufen'
            : round.endReason === 'completed'
              ? warmup
                ? 'alle Panels beschriftet'
                : 'gemeinsam fertig'
              : 'durch Moderation'}
        </h2>
        <p>
          {round.text.length} Zeichen · {countWords(round.text)} Wörter · {round.keystrokeCount} Anschläge
          {warmup && ` · ${Math.min(round.panelStarts.length, COMIC_PANELS)} von ${COMIC_PANELS} Panels`}
        </p>
        <div className="btn-row">
          <a className="btn btn--primary" href={pdfUrl(round.number)} download data-testid="export-pdf">
            ⬇ PDF der {warmup ? 'Bildrunde' : 'Runde'}
          </a>
          <a className="btn" href={zipUrl(round.number)} download data-testid="export-round">
            ⬇ Zip (Story + Keystrokes)
          </a>
          {/* The zip is also the recording: playback replays it on the
              projection, in real time (2026-09-09). Opens in its own window
              so the moderation keeps this one. */}
          <button
            className="btn btn--ghost"
            onClick={() => window.open(`${location.origin}/#/play`, 'schreibarena-playback')}
            data-testid="open-playback"
          >
            ▶ Playback öffnen
          </button>
          <button
            className="btn btn--ghost"
            onClick={() => window.open(`${location.origin}/#/clip`, 'schreibarena-clip')}
            data-testid="open-clip"
          >
            ▣ Clip fürs Netz
          </button>
        </div>
        <div className="btn-row btn-row--next">
          {warmup ? (
            <>
              <button className="btn btn--primary" onClick={() => mod({ op: 'next_round', kind: 'comic' })} data-testid="next-comic-round">
                Nächste Bildrunde →
              </button>
              <button className="btn" onClick={() => mod({ op: 'next_round', kind: 'story' })} data-testid="next-round">
                Warm-up beenden — zu den Schreibrunden →
              </button>
            </>
          ) : (
            <button className="btn btn--primary" onClick={() => mod({ op: 'next_round', kind: 'story' })} data-testid="next-round">
              {morePlanned
                ? `Runde ${round.number + 1} von ${state.plannedTotal} vorbereiten →`
                : 'Zusatzrunde vorbereiten →'}
            </button>
          )}
        </div>
        {warmup && comic ? (
          <ComicBoard
            comic={comic}
            src={comicSrc(pin, comic.id)}
            round={round}
            authors={state.authors}
            className="comicboard--mini"
          />
        ) : (
          <StoryText round={round} authors={state.authors} showCaret={false} className="story--preview" />
        )}
      </section>

      {state.pastRounds.length > 0 && (
        <section className="card">
          <span className="card__no">Archiv</span>
          <h2>Frühere Runden</h2>
          <ul className="pastlist">
            {state.pastRounds.map((r) => (
              <li key={r.number}>
                <span>
                  {r.kind === 'comic' ? `Bildrunde ${r.number}` : `Runde ${r.number}`}
                  {r.prompt ? ` — ${r.prompt}` : ''} ({r.words} Wörter)
                </span>
                <a className="btn btn--small" href={pdfUrl(r.number)} download>
                  ⬇ PDF
                </a>
                <a className="btn btn--small btn--ghost" href={zipUrl(r.number)} download>
                  Zip
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- Deck ----- */

function DeckEditor({
  state,
  mod,
  playable,
  target,
}: {
  state: PublicState;
  mod: (a: ModAction) => void;
  playable: boolean;
  target: JokerTarget;
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [durationS, setDurationS] = useState('');

  return (
    <div className="deck">
      <ul className="deck__list" data-testid="joker-deck">
        {state.jokerDeck.map((j) => (
          <li key={j.id} className="deck__item">
            {playable ? (
              <button className="btn btn--small btn--joker" onClick={() => mod({ op: 'play_joker', defId: j.id, target })} title={j.desc} data-testid={`play-${j.id}`}>
                ⚡ {j.name}
              </button>
            ) : (
              <span className="deck__name" title={j.desc}>
                {j.name}
              </span>
            )}
            <span className="deck__dur">{j.durationMs === null ? 'manuell' : formatDuration(j.durationMs)}</span>
            {!playable && (
              <button className="btn btn--ghost btn--small" onClick={() => mod({ op: 'remove_joker_def', id: j.id })} aria-label={`${j.name} entfernen`}>
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
      <form
        className="deck__add"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          mod({
            op: 'add_joker_def',
            name,
            desc,
            durationMs: durationS.trim() === '' ? undefined : durationS.trim() === '0' ? null : Number(durationS) * 1000,
          });
          setName('');
          setDesc('');
          setDurationS('');
        }}
      >
        <input className="input" placeholder="Neuer Joker…" value={name} onChange={(e) => setName(e.target.value)} data-testid="adhoc-name" />
        <input className="input" placeholder="Beschreibung (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} />
        <input className="input input--num" placeholder="Sek." title="Dauer in Sekunden; 0 = manuell beenden" inputMode="numeric" value={durationS} onChange={(e) => setDurationS(e.target.value)} />
        <button className="btn btn--small" type="submit" disabled={!name.trim()} data-testid="adhoc-add">
          + Hinzufügen
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------- Log ----- */

function EventLog({ events, state }: { events: GameEvent[]; state: PublicState }) {
  const lines = useMemo(() => [...events].reverse().map((ev) => describeEvent(ev, state)), [events, state]);
  return (
    <div className="eventlog" data-testid="event-log">
      {lines.map((line, i) => (
        <div key={events.length - i} className="eventlog__line">
          <span className="eventlog__time">{events[events.length - 1 - i]?.t.slice(11, 19)}</span>
          {line}
        </div>
      ))}
    </div>
  );
}

function describeEvent(ev: GameEvent, state: PublicState): string {
  const name = (i: unknown) => state.authors[Number(i)]?.name ?? `Autor:in ${Number(i) + 1}`;
  const panel = (d: Record<string, unknown>) => (typeof d['panel'] === 'number' ? ` Panel ${Number(d['panel']) + 1}.` : '');
  switch (ev.type) {
    case 'round_start':
      return `${ev.data['kind'] === 'comic' ? 'Bildrunde' : 'Runde'} gestartet — ${name(ev.data['firstAuthor'])} beginnt.`;
    case 'turn_pass':
      return ev.data['cause'] === 'timeout'
        ? `Zeit von ${name(ev.data['from'])} abgelaufen → ${name(ev.data['to'])}.${panel(ev.data)}`
        : `${name(ev.data['from'])} gibt ab → ${name(ev.data['to'])}.${panel(ev.data)}`;
    case 'turn_pass_undone':
      return `Zugwechsel zurückgenommen — ${name(ev.data['restoredActive'])} ist wieder dran.${panel(ev.data)}`;
    case 'pause':
      return ev.data['cause'] === 'server_restart' ? 'Pause (Server-Neustart).' : 'Pause.';
    case 'resume':
      return 'Weiter geht’s.';
    case 'author_frozen':
      return `${name(ev.data['author'])} hat keine Zeit mehr.`;
    case 'joker_start':
      return `Joker „${String(ev.data['name'])}“ gespielt (${ev.data['target'] === 'global' ? 'beide' : name(ev.data['target'])}).`;
    case 'joker_end':
      return `Joker „${String(ev.data['name'])}“ beendet.`;
    case 'penalty':
      return `Strafe: −${Math.round(Number(ev.data['ms']) / 1000)} s für ${name(ev.data['author'])}${ev.data['reason'] ? ` (${String(ev.data['reason'])})` : ''}.`;
    case 'clock_adjust':
      return `Zeitkorrektur: ${Number(ev.data['deltaMs']) > 0 ? '+' : ''}${Math.round(Number(ev.data['deltaMs']) / 1000)} s für ${name(ev.data['author'])}.`;
    case 'completion_confirmed':
      return `${name(ev.data['author'])} erklärt die Geschichte für fertig${ev.data['by'] === 'moderator' ? ' (durch Moderation)' : ''}.`;
    case 'completion_retracted':
      return `${name(ev.data['author'])} nimmt das Fertig zurück.`;
    case 'round_end':
      return `Runde beendet (${ev.data['reason'] === 'time' ? 'Zeit' : ev.data['reason'] === 'completed' ? 'fertig' : 'Moderation'}).`;
    default:
      return ev.type;
  }
}